import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ReportSummary, ReportsService } from '../../core/services/reports.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-reports',
  imports: [FormsModule],
  templateUrl: './reports.html',
  styleUrl: './reports.scss',
})
export class Reports implements OnInit {
  private readonly reports = inject(ReportsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly currentUser = inject(CurrentUserService);
  get canExport(): boolean { return this.currentUser.hasPermission('REPORT_EXPORT_EXCEL') || this.currentUser.hasPermission('REPORT_EXPORT_PDF'); }
  get canSync(): boolean { return this.currentUser.hasPermission('REPORT_SYNC'); }
  from = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  to = new Date().toISOString().slice(0, 10);
  data?: ReportSummary;
  loading = true;
  syncing = false;
  error = '';
  exporting?: 'xlsx' | 'pdf';
  reportType = 'all';
  readonly reportTypes = [
    { value: 'all', label: 'Overview', icon: 'dashboard', metrics: [] },
    { value: 'revenue', label: 'Revenue', icon: 'payments', metrics: ['revenue'] },
    { value: 'orders', label: 'Orders', icon: 'receipt_long', metrics: ['orders.created'] },
    { value: 'purchases', label: 'Purchases', icon: 'shopping_cart', metrics: ['purchases'] },
    { value: 'expenses', label: 'Expenses', icon: 'account_balance_wallet', metrics: ['expenses'] },
    {
      value: 'products',
      label: 'Products',
      icon: 'inventory_2',
      metrics: ['products.created', 'products.used.created'],
    },
    { value: 'customers', label: 'Customers', icon: 'group', metrics: ['customers.created'] },
    {
      value: 'businesses',
      label: 'Businesses',
      icon: 'storefront',
      metrics: ['businesses.created'],
    },
    { value: 'chat', label: 'Chat messages', icon: 'forum', metrics: ['chat.messages'] },
    {
      value: 'notifications',
      label: 'Notifications',
      icon: 'notifications',
      metrics: ['notifications.created'],
    },
  ];
  ngOnInit(): void {
    this.load();
  }
  load(): void {
    this.loading = true;
    this.error = '';
    this.reports
      .summary(this.from, this.to)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (data) => (this.data = data),
        error: (e) => (this.error = httpErrorMessage(e, 'Unable to load reporting data.')),
      });
  }
  sync(): void {
    if (!this.canSync) return;
    this.syncing = true;
    this.reports
      .sync()
      .pipe(finalize(() => (this.syncing = false)))
      .subscribe({
        next: () => this.load(),
        error: (e) => (this.error = httpErrorMessage(e, 'Unable to synchronize reports.')),
      });
  }
  metric(name: string): number {
    return Number(this.data?.metrics?.[name] ?? 0);
  }
  money(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  }
  number(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }
  selectedReport() {
    return this.reportTypes.find((item) => item.value === this.reportType) ?? this.reportTypes[0];
  }
  selectedTotal(): number {
    return this.selectedReport().metrics.reduce((sum, metric) => sum + this.metric(metric), 0);
  }
  selectedDaily(): Array<{ date: string; value: number }> {
    const selected = new Set(this.selectedReport().metrics);
    const rows = new Map<string, number>();
    for (const item of this.data?.trend ?? [])
      if (selected.has(item.metric)) rows.set(item.date, (rows.get(item.date) ?? 0) + item.value);
    return [...rows]
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }
  isMoneyReport(): boolean {
    return ['revenue', 'purchases', 'expenses'].includes(this.reportType);
  }
  selectedValue(value: number): string {
    return this.isMoneyReport() ? this.money(value) : this.number(value);
  }
  daily(): Array<{
    date: string;
    revenue: number;
    expenses: number;
    purchases: number;
    orders: number;
  }> {
    const rows = new Map<string, any>();
    for (const item of this.data?.trend ?? []) {
      const row = rows.get(item.date) ?? {
        date: item.date,
        revenue: 0,
        expenses: 0,
        purchases: 0,
        orders: 0,
      };
      if (item.metric === 'orders.created') row.orders += item.value;
      else row[item.metric] = (row[item.metric] ?? 0) + item.value;
      rows.set(item.date, row);
    }
    return [...rows.values()].sort((a, b) => b.date.localeCompare(a.date));
  }
  export(format: 'xlsx' | 'pdf'): void {
    if (!this.canExport) return;
    if (this.reportType === 'all') return;
    this.exporting = format;
    this.error = '';
    this.reports
      .export(format, this.reportType, this.from, this.to)
      .pipe(
        finalize(() => {
          this.exporting = undefined;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `ZepBazaar-${this.reportType}-Report-${this.from.replaceAll('-', '')}-${this.to.replaceAll('-', '')}.${format}`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: async (e) => {
          if (e?.error instanceof Blob) {
            try {
              const body = JSON.parse(await e.error.text());
              this.error =
                body.message || body.title || `Unable to export ${format.toUpperCase()} report.`;
            } catch {
              this.error = `Unable to export ${format.toUpperCase()} report.`;
            }
          } else {
            this.error = httpErrorMessage(e, `Unable to export ${format.toUpperCase()} report.`);
          }
          this.cdr.detectChanges();
        },
      });
  }
}
